import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, ArrowDown, ArrowUp, ClipboardCopy, Copy, ExternalLink, Eye, EyeOff, Info, Link2, Lock, Plus, Redo2, RotateCw, Save, Trash2, Undo2, Unlock, Upload, X } from 'lucide-react'
import { clockApi } from '../../lib/api.js'
import { useActiveChannel, useActiveChannelId, useAuthUser } from '../../store/index.js'
import type { ClockDisplay, ClockDisplayKind, ClockLayoutConfig, ClockOverflowMode, ClockTemplate, ClockTheme, ClockTimePrecision, ClockWidget, ClockWidgetType, ScheduleItem } from '../../types/domain.js'
import { CLOCK_SCALE_MAX_PCT, CLOCK_SCALE_MIN_PCT, resolveClockScalePct } from '../../lib/clockFontScale.js'
import { forgetLaunchPath, pruneLaunchPaths, readLaunchPath, rememberLaunchPath, toAbsoluteLaunchUrl } from '../../lib/clockLaunchLinks.js'
import { PageHeader, PageTabs } from '../common/PageChrome.js'
import { Alert, AlertDescription } from '../ui/alert.js'
import { Button } from '../ui/button.js'
import { Field, FieldLabel } from '../ui/field.js'
import { Input } from '../ui/input.js'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '../ui/select.js'
import { Switch } from '../ui/switch.js'
import { ClockCanvas } from './ClockCanvas.js'
import './clock-admin.css'

const WIDGET_TYPES: Array<{ type: ClockWidgetType; label: string }> = [
  ['CLOCK', 'Horloge antenne'], ['DATE', 'Date'], ['ON_AIR', 'À l’antenne'], ['REMAINING', 'Temps restant'], ['ELAPSED', 'Temps écoulé'], ['NEXT_ITEM', 'Prochain élément'], ['NEXT_LIVE', 'Prochain direct'], ['TOP', 'Top antenne'], ['LIVE_END', 'Fin du direct'], ['LIVE_PART', 'Partie du direct'], ['PROGRESS', 'Progression'], ['RUNDOWN', 'Mini-conducteur'], ['VIDEO_RETURN', 'Retour antenne'], ['LIVE_SOURCE', 'État source live'], ['SYSTEM_STATUS', 'État système'], ['ALERTS', 'Alertes'], ['TEXT', 'Texte libre'], ['CHANNEL_IDENTITY', 'Identité chaîne'],
].map(([type, label]) => ({ type: type as ClockWidgetType, label }))
const TIME_WIDGET_TYPES = new Set<ClockWidgetType>(['CLOCK', 'REMAINING', 'ELAPSED', 'NEXT_ITEM', 'NEXT_LIVE', 'TOP', 'LIVE_END', 'LIVE_PART'])

const emptyItems: ScheduleItem[] = []
const clone = <T,>(value: T): T => structuredClone(value)

interface ClockSelectOption {
  value: string
  label: string
}

const ClockSelect = memo(function ClockSelect({
  value,
  options,
  placeholder,
  ariaLabel,
  className,
  id,
  onValueChange,
}: {
  value: string
  options: ClockSelectOption[]
  placeholder?: string
  ariaLabel: string
  className?: string
  id?: string
  onValueChange: (value: string) => void
}) {
  return <Select value={value} onValueChange={onValueChange}>
    <SelectTrigger id={id} size="sm" aria-label={ariaLabel} className={className}>
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
    <SelectContent position="popper" align="start" className="clock-select__content">
      <SelectGroup>
        {options.map(option => <SelectItem key={option.value} value={option.value} className="clock-select__item">{option.label}</SelectItem>)}
      </SelectGroup>
    </SelectContent>
  </Select>
})

/** Les trois parties dimensionnables d'un widget. */
const SCALE_FIELDS = [
  { key: 'valueScale', label: 'Valeur (% hauteur)' },
  { key: 'labelScale', label: 'Libellé (% hauteur)' },
  { key: 'titleScale', label: 'Titre (% hauteur)' },
] as const

export const ClockAdminView = memo(function ClockAdminView() {
  const channelId = useActiveChannelId()
  const channel = useActiveChannel()
  const user = useAuthUser()
  const canManage = user?.role === 'ADMIN' || user?.permissions.includes('CLOCK_MANAGE')
  const [tab, setTab] = useState('templates')
  const [templates, setTemplates] = useState<ClockTemplate[]>([])
  const [displays, setDisplays] = useState<ClockDisplay[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [draft, setDraft] = useState<ClockLayoutConfig | null>(null)
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [history, setHistory] = useState<ClockLayoutConfig[]>([])
  const [future, setFuture] = useState<ClockLayoutConfig[]>([])
  const [previewTheme, setPreviewTheme] = useState<ClockTheme>('DARK')
  const [simulation, setSimulation] = useState(false)
  const [simulationOffset, setSimulationOffset] = useState(0)
  const [simulationCase, setSimulationCase] = useState<'UPCOMING' | 'WAITING' | 'LIVE' | 'OVERRUN'>('UPCOMING')
  const [templateName, setTemplateName] = useState('')
  const [message, setMessage] = useState('')
  const [newScreen, setNewScreen] = useState({ name: '', locationLabel: '', displayKind: 'CONTROL_ROOM' as ClockDisplayKind, templateId: '' })
  const stageRef = useRef<HTMLDivElement>(null)

  const load = useCallback(async () => {
    if (!channelId) return
    const [nextTemplates, nextDisplays] = await Promise.all([clockApi.templates(channelId), clockApi.displays(channelId)])
    setTemplates(nextTemplates); setDisplays(nextDisplays)
    // Un lien retenu pour un écran supprimé n'ouvre plus rien : on ne garde pas
    // un secret sans objet sur le poste.
    pruneLaunchPaths(nextDisplays.map(display => display.id))
    setSelectedId(current => current && nextTemplates.some(template => template.id === current) ? current : nextTemplates[0]?.id ?? null)
  }, [channelId])
  useEffect(() => { void load().catch(error => setMessage(error instanceof Error ? error.message : 'Chargement impossible')) }, [load])

  const selectedTemplate = useMemo(() => templates.find(template => template.id === selectedId) ?? null, [selectedId, templates])
  useEffect(() => {
    if (!selectedTemplate) { setDraft(null); return }
    setDraft(clone(selectedTemplate.draftConfig)); setTemplateName(selectedTemplate.name); setSelectedWidgetId(null); setDirty(false); setHistory([]); setFuture([]); setPreviewTheme(selectedTemplate.defaultTheme)
  }, [selectedTemplate?.id])
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => { if (dirty) { event.preventDefault(); event.returnValue = '' } }
    window.addEventListener('beforeunload', warn); return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const commit = useCallback((next: ClockLayoutConfig) => {
    if (!draft) return
    setHistory(values => [...values.slice(-99), clone(draft)])
    setFuture([]); setDraft(next); setDirty(true)
  }, [draft])
  const updateWidget = useCallback((id: string, transform: (widget: ClockWidget) => ClockWidget) => {
    if (!draft) return
    commit({ ...draft, widgets: draft.widgets.map(widget => widget.id === id ? transform(widget) : widget) })
  }, [commit, draft])
  const undo = useCallback(() => {
    const previous = history[history.length - 1]; if (!previous || !draft) return
    setFuture(values => [clone(draft), ...values]); setDraft(previous); setHistory(values => values.slice(0, -1)); setDirty(true)
  }, [draft, history])
  const redo = useCallback(() => {
    const next = future[0]; if (!next || !draft) return
    setHistory(values => [...values, clone(draft)]); setDraft(next); setFuture(values => values.slice(1)); setDirty(true)
  }, [draft, future])

  const handlePointerDown = useCallback((event: React.PointerEvent, widget: ClockWidget, mode: 'move' | 'resize') => {
    if (!draft || widget.locked || !stageRef.current) return
    event.preventDefault()
    const rectangle = stageRef.current.getBoundingClientRect()
    const start = { x: event.clientX, y: event.clientY, widget: clone(widget), draft: clone(draft) }
    const move = (pointer: PointerEvent) => {
      const dx = (pointer.clientX - start.x) * 1920 / rectangle.width
      const dy = (pointer.clientY - start.y) * 1080 / rectangle.height
      const snap = (value: number) => Math.round(value / 8) * 8
      const changed = mode === 'move'
        ? { ...start.widget, x: Math.max(0, Math.min(1920 - start.widget.width, snap(start.widget.x + dx))), y: Math.max(0, Math.min(1080 - start.widget.height, snap(start.widget.y + dy))) }
        : { ...start.widget, width: Math.max(40, Math.min(1920 - start.widget.x, snap(start.widget.width + dx))), height: Math.max(32, Math.min(1080 - start.widget.y, snap(start.widget.height + dy))) }
      setDraft({ ...start.draft, widgets: start.draft.widgets.map(value => value.id === widget.id ? changed : value) })
      setDirty(true)
    }
    const up = () => {
      setHistory(values => [...values.slice(-99), start.draft]); setFuture([])
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up)
  }, [draft])
  const handleKeyDown = useCallback((event: React.KeyboardEvent, widget: ClockWidget) => {
    const delta = event.shiftKey ? 8 : 1
    const movement: Record<string, [number, number]> = { ArrowLeft: [-delta, 0], ArrowRight: [delta, 0], ArrowUp: [0, -delta], ArrowDown: [0, delta] }
    if (!movement[event.key] || widget.locked) return
    event.preventDefault(); const [dx, dy] = movement[event.key]
    updateWidget(widget.id, value => ({ ...value, x: Math.max(0, Math.min(1920 - value.width, value.x + dx)), y: Math.max(0, Math.min(1080 - value.height, value.y + dy)) }))
  }, [updateWidget])

  const addWidget = useCallback((type: ClockWidgetType) => {
    if (!draft) return
    const id = `${type.toLowerCase()}-${crypto.randomUUID()}`
    const entry = WIDGET_TYPES.find(value => value.type === type)!
    const widget: ClockWidget = { id, type, name: entry.label, x: 800, y: 440, width: 320, height: 200, visible: true, locked: false, style: { align: 'CENTER', size: 'LG', weight: 'BOLD', surface: 'SUBTLE', colorRole: 'DEFAULT', showLabel: true }, config: { warningSec: 600, dangerSec: 120, endWarningSec: 300, endDangerSec: 60, tenthsBelowSec: 10 } }
    commit({ ...draft, widgets: [...draft.widgets, widget] }); setSelectedWidgetId(id)
  }, [commit, draft])
  const selectedWidget = draft?.widgets.find(widget => widget.id === selectedWidgetId) ?? null
  const duplicateSelected = useCallback(() => {
    if (!draft || !selectedWidget) return
    const copy = { ...clone(selectedWidget), id: `${selectedWidget.type.toLowerCase()}-${crypto.randomUUID()}`, name: `${selectedWidget.name} — copie`, x: Math.min(1920 - selectedWidget.width, selectedWidget.x + 16), y: Math.min(1080 - selectedWidget.height, selectedWidget.y + 16) }
    commit({ ...draft, widgets: [...draft.widgets, copy] }); setSelectedWidgetId(copy.id)
  }, [commit, draft, selectedWidget])

  const save = useCallback(async () => {
    if (!selectedTemplate || !draft) return
    const updated = await clockApi.saveDraft(selectedTemplate.id, { name: templateName, draftConfig: draft })
    setTemplates(values => values.map(value => value.id === updated.id ? updated : value)); setDirty(false); setMessage('Brouillon enregistré — les écrans publiés sont inchangés.')
  }, [draft, selectedTemplate, templateName])
  const publish = useCallback(async () => {
    if (!selectedTemplate) return
    if (dirty) await save()
    if (!window.confirm(`Publier ce modèle sur ${selectedTemplate._count?.displays ?? 0} écran(s) ?`)) return
    await clockApi.publish(selectedTemplate.id); await load(); setMessage('Version publiée. Les écrans ouverts vont se mettre à jour.')
  }, [dirty, load, save, selectedTemplate])

  const createScreen = useCallback(async () => {
    if (!channelId || !newScreen.name || !newScreen.templateId) return
    const created = await clockApi.createDisplay({ channelId, ...newScreen })
    // Le serveur ne garde qu'une empreinte du token : si on ne retient pas le
    // lien ici, il est définitivement perdu à la fermeture de la page.
    rememberLaunchPath(created.display.id, created.launchUrl)
    await navigator.clipboard?.writeText(toAbsoluteLaunchUrl(created.launchUrl, window.location.origin)).catch(() => undefined)
    setMessage(`Lien créé et copié. Il reste accessible depuis la carte de l’écran, sur ce poste.`)
    setNewScreen(value => ({ ...value, name: '', locationLabel: '' })); await load()
  }, [channelId, load, newScreen])
  const rotate = useCallback(async (display: ClockDisplay) => {
    if (!window.confirm(`Révoquer immédiatement l’ancien lien de « ${display.name} » ?`)) return
    const result = await clockApi.rotateToken(display.id)
    rememberLaunchPath(display.id, result.launchUrl)
    await navigator.clipboard?.writeText(toAbsoluteLaunchUrl(result.launchUrl, window.location.origin)).catch(() => undefined)
    setMessage('Nouveau lien copié. L’ancien est révoqué : les écrans qui l’utilisaient doivent être rouverts.'); await load()
  }, [load])
  const simulationItems = useMemo(() => {
    if (!simulation) return emptyItems
    const now = Date.now()
    const start = simulationCase === 'UPCOMING' ? now + 5 * 60_000 : simulationCase === 'WAITING' ? now - 30_000 : now - 5 * 60_000
    const end = simulationCase === 'OVERRUN' ? now - 60_000 : now + 25 * 60_000
    return [{ id: 'clock-simulation-live', itemType: 'LIVE', status: simulationCase === 'UPCOMING' ? 'SCHEDULED' : 'ON_AIR', startTime: new Date(start).toISOString(), endTime: new Date(end).toISOString(), liveStartMode: simulationCase === 'WAITING' ? 'MANUAL' : 'AUTO', liveEndMode: simulationCase === 'OVERRUN' ? 'MANUAL' : 'AUTO', liveGoLiveAt: simulationCase === 'WAITING' ? null : new Date(start).toISOString(), program: { title: 'Journal du soir' } } as ScheduleItem]
  }, [simulation, simulationCase])
  const layoutWarning = useMemo(() => {
    if (!draft) return ''
    const outside = draft.widgets.find(widget => widget.x < 0 || widget.y < 0 || widget.x + widget.width > 1920 || widget.y + widget.height > 1080)
    if (outside) return `${outside.name} sort de la toile.`
    for (let frontIndex = 1; frontIndex < draft.widgets.length; frontIndex += 1) {
      const front = draft.widgets[frontIndex]
      for (let backIndex = 0; backIndex < frontIndex; backIndex += 1) {
        const back = draft.widgets[backIndex]
        if (front.x <= back.x && front.y <= back.y && front.x + front.width >= back.x + back.width && front.y + front.height >= back.y + back.height) return `${front.name} recouvre entièrement ${back.name}.`
      }
    }
    return ''
  }, [draft])

  if (!channelId || !channel) return <div className="tp-page-shell"><PageHeader title="Clock" /><div className="tp-empty-state">Sélectionnez une chaîne.</div></div>

  return <div className="tp-page-shell clock-admin">
    <PageHeader
      title="Clock"
      actions={tab === 'templates' && canManage ? <>
        <Button size="sm" variant="outline" onClick={() => void save()} disabled={!dirty}><Save data-icon="inline-start" />Enregistrer</Button>
        <Button size="sm" onClick={() => void publish()}><Upload data-icon="inline-start" />Publier</Button>
      </> : undefined}
    />
    <PageTabs items={[{ id: 'templates', label: 'Modèles' }, { id: 'displays', label: 'Écrans' }]} activeId={tab} onChange={setTab} ariaLabel="Gestion Clock" controlsId="clock-panel" idPrefix="clock-tab" />
    <div className="clock-admin__feedback" aria-live="polite">
      {message && <Alert className="clock-admin__message" role="status">
        <Info aria-hidden="true" />
        <AlertDescription>{message}</AlertDescription>
        <Button size="icon-xs" variant="ghost" aria-label="Masquer le message" title="Masquer le message" onClick={() => setMessage('')}><X /></Button>
      </Alert>}
    </div>
    <div id="clock-panel" role="tabpanel">
      {tab === 'templates' && <div className="clock-editor">
        <aside className="clock-editor__sidebar">
          <div className="clock-editor__title">
            <strong>Modèles</strong>
            {canManage && <Button size="icon-xs" variant="ghost" aria-label="Nouveau modèle" title="Nouveau modèle" onClick={async () => { const value = await clockApi.createTemplate({ channelId, name: 'Nouveau modèle', displayKind: 'CONTROL_ROOM', defaultTheme: 'DARK' }); await load(); setSelectedId(value.id) }}><Plus /></Button>}
          </div>
          {templates.map(template => <button key={template.id} className="clock-editor__template" data-active={template.id === selectedId || undefined} onClick={() => { if (!dirty || window.confirm('Abandonner les modifications non enregistrées ?')) setSelectedId(template.id) }}>
            <strong>{template.name}</strong>
            <span>{template.displayKind === 'CONTROL_ROOM' ? 'Régie' : 'Plateau'} · v{template.publishedVersion || '—'} · {template._count?.displays ?? 0} écran(s)</span>
          </button>)}
          {canManage && selectedTemplate && <div className="clock-editor__actions clock-editor__sidebar-actions">
            <Button size="xs" variant="outline" onClick={async () => { const value = await clockApi.duplicate(selectedTemplate.id); await load(); setSelectedId(value.id) }}><Copy data-icon="inline-start" />Dupliquer</Button>
            <Button size="icon-xs" variant="destructive" aria-label="Supprimer le modèle" title="Supprimer le modèle" disabled={Boolean(selectedTemplate._count?.displays)} onClick={async () => { if (!window.confirm(`Supprimer « ${selectedTemplate.name} » ?`)) return; await clockApi.deleteTemplate(selectedTemplate.id); await load() }}><Trash2 /></Button>
          </div>}
        </aside>

        <section className="clock-editor__workspace">
          <div className="clock-editor__toolbar">
            {canManage && <div className="clock-editor__toolbar-group">
              <Button size="icon-sm" variant="outline" aria-label="Annuler" title="Annuler" onClick={undo} disabled={!history.length}><Undo2 /></Button>
              <Button size="icon-sm" variant="outline" aria-label="Rétablir" title="Rétablir" onClick={redo} disabled={!future.length}><Redo2 /></Button>
              <ClockSelect value="" ariaLabel="Ajouter un widget" placeholder="Ajouter un widget…" options={WIDGET_TYPES.map(option => ({ value: option.type, label: option.label }))} onValueChange={value => addWidget(value as ClockWidgetType)} className="clock-editor__widget-select" />
            </div>}
            <div className="clock-editor__toolbar-group">
              <Button size="sm" variant="outline" onClick={() => setPreviewTheme(value => value === 'DARK' ? 'LIGHT' : 'DARK')}>{previewTheme === 'DARK' ? 'Aperçu clair' : 'Aperçu sombre'}</Button>
              <label className="clock-editor__toggle">Simulation <Switch size="sm" checked={simulation} onCheckedChange={setSimulation} /></label>
            </div>
            {simulation && <div className="clock-editor__toolbar-group">
              <ClockSelect
                value={simulationCase}
                ariaLabel="État de simulation"
                options={[{ value: 'UPCOMING', label: 'Avant direct' }, { value: 'WAITING', label: 'Attente du top' }, { value: 'LIVE', label: 'En direct' }, { value: 'OVERRUN', label: 'Dépassement' }]}
                onValueChange={value => setSimulationCase(value as typeof simulationCase)}
              />
              <input className="clock-editor__range" type="range" min={-900} max={1800} step={1} value={simulationOffset} onChange={event => setSimulationOffset(Number(event.target.value))} aria-label="Décalage de simulation en secondes" />
            </div>}
            {layoutWarning && <Alert className="clock-editor__warning">
              <AlertTriangle aria-hidden="true" />
              <AlertDescription>{layoutWarning}</AlertDescription>
            </Alert>}
          </div>
          {draft && <div className="clock-editor__stage-wrap" data-theme={previewTheme === 'DARK' ? 'antenne' : 'clair'}>
            <div className="clock-editor__stage" ref={stageRef}>
              <ClockCanvas config={draft} channel={channel} items={simulationItems} playout={null} nowMs={Date.now() + simulationOffset * 1000} editing={canManage} simulation={simulation} selectedId={selectedWidgetId} onSelect={setSelectedWidgetId} onPointerDown={canManage ? handlePointerDown : undefined} onKeyDown={canManage ? handleKeyDown : undefined} />
            </div>
          </div>}
        </section>

        <aside className="clock-editor__inspector">
          <strong>{canManage ? 'Inspecteur' : 'Consultation'}</strong>
          {canManage && <Field>
            <FieldLabel htmlFor="clock-template-name">Nom du modèle</FieldLabel>
            <Input id="clock-template-name" value={templateName} onChange={event => { setTemplateName(event.target.value); setDirty(true) }} />
          </Field>}
          {canManage && selectedWidget ? <>
            <Field>
              <FieldLabel htmlFor="clock-widget-name">Nom du widget</FieldLabel>
              <Input id="clock-widget-name" value={selectedWidget.name} onChange={event => updateWidget(selectedWidget.id, value => ({ ...value, name: event.target.value }))} />
            </Field>
            <div className="clock-editor__geometry">{(['x', 'y', 'width', 'height'] as const).map(key => <Field key={key}>
              <FieldLabel htmlFor={`clock-widget-${key}`}>{key.toUpperCase()}</FieldLabel>
              <Input id={`clock-widget-${key}`} type="number" value={selectedWidget[key]} onChange={event => updateWidget(selectedWidget.id, value => ({ ...value, [key]: Number(event.target.value) }))} />
            </Field>)}</div>
            <label className="clock-editor__toggle clock-editor__toggle--row">Afficher le libellé <Switch size="sm" checked={selectedWidget.style.showLabel} onCheckedChange={checked => updateWidget(selectedWidget.id, value => ({ ...value, style: { ...value.style, showLabel: checked } }))} /></label>
            <div className="clock-editor__actions clock-editor__widget-actions">
              <Button size="icon-sm" variant="outline" aria-label={selectedWidget.locked ? 'Déverrouiller le widget' : 'Verrouiller le widget'} title={selectedWidget.locked ? 'Déverrouiller' : 'Verrouiller'} onClick={() => updateWidget(selectedWidget.id, value => ({ ...value, locked: !value.locked }))}>{selectedWidget.locked ? <Unlock /> : <Lock />}</Button>
              <Button size="icon-sm" variant="outline" aria-label={selectedWidget.visible ? 'Masquer le widget' : 'Afficher le widget'} title={selectedWidget.visible ? 'Masquer' : 'Afficher'} onClick={() => updateWidget(selectedWidget.id, value => ({ ...value, visible: !value.visible }))}>{selectedWidget.visible ? <Eye /> : <EyeOff />}</Button>
              <Button size="icon-sm" variant="outline" aria-label="Dupliquer le widget" title="Dupliquer" onClick={duplicateSelected}><Copy /></Button>
              <Button size="icon-sm" variant="outline" aria-label="Placer au premier plan" title="Premier plan" onClick={() => commit({ ...draft!, widgets: [...draft!.widgets.filter(value => value.id !== selectedWidget.id), selectedWidget] })}><ArrowUp /></Button>
              <Button size="icon-sm" variant="outline" aria-label="Placer à l’arrière-plan" title="Arrière-plan" onClick={() => commit({ ...draft!, widgets: [selectedWidget, ...draft!.widgets.filter(value => value.id !== selectedWidget.id)] })}><ArrowDown /></Button>
              <Button size="icon-sm" variant="destructive" aria-label="Supprimer le widget" title="Supprimer" onClick={() => { commit({ ...draft!, widgets: draft!.widgets.filter(value => value.id !== selectedWidget.id) }); setSelectedWidgetId(null) }}><Trash2 /></Button>
            </div>
            {/* Tailles libres, en % de la hauteur de la boîte : agrandir le
                widget agrandit son texte. Un champ vidé revient à la taille
                héritée de l'ancien palier, sans rien déplacer à l'écran. */}
            <div className="clock-editor__scales">
              {SCALE_FIELDS.map(({ key, label }) => {
                const inherited = resolveClockScalePct(selectedWidget)[key === 'valueScale' ? 'value' : key === 'labelScale' ? 'label' : 'title']
                const custom = selectedWidget.style[key]
                return (
                  <Field key={key}>
                    <FieldLabel htmlFor={`clock-widget-${key}`}>{label}</FieldLabel>
                    <Input
                      id={`clock-widget-${key}`}
                      type="number"
                      min={CLOCK_SCALE_MIN_PCT}
                      max={CLOCK_SCALE_MAX_PCT}
                      step={1}
                      value={custom ?? Math.round(inherited * 10) / 10}
                      placeholder={String(Math.round(inherited))}
                      onChange={event => updateWidget(selectedWidget.id, widget => {
                        const style = { ...widget.style }
                        const raw = event.target.value
                        if (raw === '') delete style[key]
                        else style[key] = Math.min(CLOCK_SCALE_MAX_PCT, Math.max(CLOCK_SCALE_MIN_PCT, Number(raw)))
                        return { ...widget, style }
                      })}
                    />
                  </Field>
                )
              })}
            </div>
            <Field>
              <FieldLabel htmlFor="clock-widget-overflow">Texte trop long</FieldLabel>
              <ClockSelect
                id="clock-widget-overflow"
                value={selectedWidget.style.overflow ?? 'SHRINK'}
                ariaLabel="Comportement d’un texte trop long"
                options={[{ value: 'SHRINK', label: 'Réduire pour tenir' }, { value: 'WRAP', label: 'Passer à la ligne' }, { value: 'CLIP', label: 'Couper (…)' }]}
                onValueChange={value => updateWidget(selectedWidget.id, widget => ({ ...widget, style: { ...widget.style, overflow: value as ClockOverflowMode } }))}
              />
            </Field>
            {TIME_WIDGET_TYPES.has(selectedWidget.type) && <Field>
              <FieldLabel htmlFor="clock-widget-precision">Précision du chrono</FieldLabel>
              <ClockSelect
                id="clock-widget-precision"
                value={selectedWidget.config.timePrecision ?? 'AUTO'}
                ariaLabel="Précision du chrono"
                options={[{ value: 'AUTO', label: 'Automatique' }, { value: 'SECONDS', label: 'Secondes' }, { value: 'TENTHS', label: 'Dixièmes (.0)' }, { value: 'HUNDREDTHS', label: 'Centièmes (.00)' }, { value: 'MILLISECONDS', label: 'Millisecondes (.000)' }]}
                onValueChange={value => updateWidget(selectedWidget.id, widget => {
                  const config = { ...widget.config }
                  if (value === 'AUTO') delete config.timePrecision
                  else config.timePrecision = value as ClockTimePrecision
                  return { ...widget, config }
                })}
              />
            </Field>}
            <Field>
              <FieldLabel htmlFor="clock-widget-color">Rôle de couleur</FieldLabel>
              <ClockSelect id="clock-widget-color" value={selectedWidget.style.colorRole} ariaLabel="Rôle de couleur" options={[{ value: 'DEFAULT', label: 'Standard' }, { value: 'MUTED', label: 'Discret' }, { value: 'ACCENT', label: 'Accent' }, { value: 'SUCCESS', label: 'Succès' }, { value: 'WARNING', label: 'Attention' }, { value: 'DANGER', label: 'Danger' }, { value: 'ON_AIR', label: 'À l’antenne' }]} onValueChange={value => updateWidget(selectedWidget.id, widget => ({ ...widget, style: { ...widget.style, colorRole: value as ClockWidget['style']['colorRole'] } }))} />
            </Field>
          </> : <p className="clock-editor__hint">{canManage ? 'Sélectionnez un widget sur la toile.' : 'Les modèles sont en lecture seule avec votre permission Clock.'}</p>}
        </aside>
      </div>}

      {tab === 'displays' && <section className="clock-displays">
        {canManage && <form className="clock-displays__create" onSubmit={event => { event.preventDefault(); void createScreen() }}>
          <Field>
            <FieldLabel htmlFor="clock-screen-name">Nom de l’écran</FieldLabel>
            <Input id="clock-screen-name" placeholder="Écran régie principal" value={newScreen.name} onChange={event => setNewScreen(value => ({ ...value, name: event.target.value }))} />
          </Field>
          <Field>
            <FieldLabel htmlFor="clock-screen-location">Emplacement</FieldLabel>
            <Input id="clock-screen-location" placeholder="Régie 1" value={newScreen.locationLabel} onChange={event => setNewScreen(value => ({ ...value, locationLabel: event.target.value }))} />
          </Field>
          <Field>
            <FieldLabel htmlFor="clock-screen-kind">Type</FieldLabel>
            <ClockSelect id="clock-screen-kind" value={newScreen.displayKind} ariaLabel="Type d’écran" options={[{ value: 'CONTROL_ROOM', label: 'Régie' }, { value: 'STUDIO', label: 'Plateau' }]} onValueChange={value => setNewScreen(current => ({ ...current, displayKind: value as ClockDisplayKind, templateId: '' }))} />
          </Field>
          <Field>
            <FieldLabel htmlFor="clock-screen-template">Modèle publié</FieldLabel>
            <ClockSelect id="clock-screen-template" value={newScreen.templateId} ariaLabel="Modèle publié" placeholder="Choisir un modèle…" options={templates.filter(value => value.publishedConfig && value.displayKind === newScreen.displayKind).map(value => ({ value: value.id, label: value.name }))} onValueChange={templateId => setNewScreen(value => ({ ...value, templateId }))} />
          </Field>
          <Button size="sm" type="submit" disabled={!newScreen.name.trim() || !newScreen.templateId}><Link2 data-icon="inline-start" />Créer le lien</Button>
        </form>}

        <div className="clock-displays__grid">{displays.map(display => <article key={display.id} className="clock-display-card">
          <div className="clock-display-card__heading">
            <strong>{display.name}</strong>
            <span>{display.locationLabel || 'Emplacement non renseigné'} · token …{display.accessTokenHint}</span>
          </div>
          <div className="clock-display-card__meta">
            <span>{display.displayKind === 'CONTROL_ROOM' ? 'Régie' : 'Plateau'}</span>
            <span>{display.template?.name}</span>
            <span>Dernière connexion {display.lastSeenAt ? new Date(display.lastSeenAt).toLocaleString('fr-FR') : 'jamais'}</span>
          </div>
          {canManage && <div className="clock-display-card__actions">
            <Field>
              <FieldLabel htmlFor={`clock-display-template-${display.id}`}>Modèle</FieldLabel>
              <ClockSelect id={`clock-display-template-${display.id}`} value={display.templateId} ariaLabel={`Modèle de ${display.name}`} options={templates.filter(value => value.publishedConfig && value.displayKind === display.displayKind).map(value => ({ value: value.id, label: value.name }))} onValueChange={templateId => void clockApi.updateDisplay(display.id, { templateId }).then(load)} />
            </Field>
            <Field>
              <FieldLabel htmlFor={`clock-display-theme-${display.id}`}>Thème</FieldLabel>
              <ClockSelect id={`clock-display-theme-${display.id}`} value={display.themeOverride ?? 'MODEL'} ariaLabel={`Thème de ${display.name}`} options={[{ value: 'MODEL', label: 'Thème du modèle' }, { value: 'LIGHT', label: 'Clair' }, { value: 'DARK', label: 'Sombre' }]} onValueChange={theme => void clockApi.updateDisplay(display.id, { themeOverride: theme === 'MODEL' ? null : theme as ClockTheme }).then(load)} />
            </Field>
            <label className="clock-editor__toggle">Actif <Switch size="sm" checked={display.active} onCheckedChange={active => void clockApi.updateDisplay(display.id, { active }).then(load)} /></label>
            <label className="clock-editor__toggle">Son <Switch size="sm" disabled={display.displayKind === 'STUDIO'} checked={display.audibleAlerts} onCheckedChange={audibleAlerts => void clockApi.updateDisplay(display.id, { audibleAlerts }).then(load)} /></label>
            <ClockLinkActions display={display} onMessage={setMessage} />
            <Button size="xs" variant="outline" onClick={() => void rotate(display)}><RotateCw data-icon="inline-start" />Régénérer</Button>
            <Button size="icon-xs" variant="destructive" aria-label={`Supprimer ${display.name}`} title="Supprimer l’écran" onClick={() => { if (window.confirm(`Supprimer ${display.name} ?`)) { forgetLaunchPath(display.id); void clockApi.deleteDisplay(display.id).then(load) } }}><Trash2 /></Button>
          </div>}
        </article>)}</div>
      </section>}
    </div>
  </div>
})

/**
 * Récupération du lien d'un écran : copie, ou ouverture directe dans un onglet.
 *
 * Le lien n'est connu que des postes qui l'ont vu délivrer — le serveur n'en
 * garde qu'une empreinte. Quand ce poste ne l'a pas, on le dit franchement et on
 * renvoie vers « Régénérer », au lieu de laisser un bouton mort.
 */
const ClockLinkActions = memo(function ClockLinkActions({ display, onMessage }: {
  display: ClockDisplay
  onMessage: (message: string) => void
}) {
  const [busy, setBusy] = useState<'copy' | 'open' | null>(null)

  /**
   * Le lien vient du serveur, qui en garde une copie scellée depuis sa création.
   * On se rabat sur la mémoire de ce poste pour les écrans créés avant cet
   * enregistrement : leur token n'a jamais été scellé et le serveur répond 409.
   */
  const resolve = useCallback(async (): Promise<string | null> => {
    try {
      const result = await clockApi.launchUrl(display.id)
      rememberLaunchPath(display.id, result.launchUrl)
      return toAbsoluteLaunchUrl(result.launchUrl, window.location.origin)
    } catch (error) {
      const local = readLaunchPath(display.id)
      if (local) return toAbsoluteLaunchUrl(local, window.location.origin)
      onMessage(error instanceof Error ? error.message : `Lien de « ${display.name} » indisponible : régénérez-le.`)
      return null
    }
  }, [display.id, display.name, onMessage])

  const copy = useCallback(async () => {
    setBusy('copy')
    try {
      const url = await resolve()
      if (!url) return
      try {
        await navigator.clipboard.writeText(url)
        onMessage(`Lien de « ${display.name} » copié.`)
      } catch {
        // Presse-papiers refusé (hors HTTPS, permission navigateur) : on affiche
        // le lien pour qu'il reste sélectionnable à la main.
        onMessage(`Copie impossible depuis ce navigateur. Lien : ${url}`)
      }
    } finally {
      setBusy(null)
    }
  }, [display.name, onMessage, resolve])

  const open = useCallback(async () => {
    setBusy('open')
    try {
      const url = await resolve()
      if (url) window.open(url, '_blank', 'noopener')
    } finally {
      setBusy(null)
    }
  }, [resolve])

  return <>
    <Button size="xs" variant="outline" disabled={busy !== null} onClick={() => void copy()}>
      <ClipboardCopy data-icon="inline-start" />{busy === 'copy' ? 'Copie…' : 'Copier le lien'}
    </Button>
    <Button size="xs" variant="outline" disabled={busy !== null} onClick={() => void open()}>
      <ExternalLink data-icon="inline-start" />{busy === 'open' ? 'Ouverture…' : 'Ouvrir'}
    </Button>
  </>
})
